/*
 * sniarti-base.js — shared cross-brand behaviour layer.
 * Copy verbatim across brands. Order matters: reveal engine first (defines
 * globals), then the IIFE callers, then standalone behaviours.
 * Loaded with data-template="{{ template.name }}" so the reveal code can read
 * the current template via document.currentScript.
 */

/* ===== reveal engine ===== */
function reveal_init() {
  text_split_nodes_to_delayed_spans();
  reveal(0);
}
var reveal_scroll_ticking = false;
function reveal_scroll() {
  // rAF-throttle so we read layout (reveal()) at most once per frame instead of
  // on every scroll event — keeps scrolling smooth (INP) on long pages.
  if (reveal_scroll_ticking) return;
  reveal_scroll_ticking = true;
  requestAnimationFrame(function () {
    reveal_scroll_ticking = false;
    reveal(80);
  });
}
function reveal(offset) {
  var windowHeight = window.innerHeight;
  var reveals = document.querySelectorAll("[class*=reveal]");
  var SECTION_STEP_MS = 250; // stagger between elements revealed in the same pass
  var batch = 0;
  reveals.forEach(reveal => {
    if (reveal.classList.contains('active') || [...reveal.classList].includes('hidden')) {
      return;
    }
    var elementTop = reveal.getBoundingClientRect().top;
    if (elementTop < windowHeight - offset) {
      // Elements that become visible together cascade top→bottom (like text
      // being written down) rather than all starting at once. The base resets
      // each pass, so a single line scrolled into view later isn't delayed.
      reveal.style.setProperty('--reveal-base', (batch * SECTION_STEP_MS) + 'ms');
      batch++;
      reveal.classList.add('active');
    }
  });
}

function split_node_to_delayed_spans(n, interval) {
  chars = n.textContent.replace(/ +/g, " ").trim().split("");
  chars_map = { '\n': '<br>',
                ' ' : '&nbsp' }
  spans = chars.map(s => `<span>${chars_map[s] ?? s}</span>`);
  spans = spans.map((s, i) => add_delay_style(s, i, interval));
  return spans;
}
function add_delay_style(span, sequence, interval) {
  if (span.match(/<span>/g)) {
    var ds = sequence * interval;
    // Per-letter delay plus the element's --reveal-base (set by reveal()) so
    // grouped text cascades top→bottom instead of every line starting together.
    var o = ` style="transition-delay: calc(var(--reveal-base, 0ms) + ${ds}ms);"`;
    span = span.replace(/<span>/, `<span${o}>`);
  }
  return span;
}
function text_split_nodes_to_delayed_spans() {
  let to_be_split = document.querySelectorAll('.reveal_fadein_letter');
  [...to_be_split].forEach(ts_node => {
    interval = 'delay_interval_ms' in ts_node.attributes ? parseFloat(ts_node.attributes.delay_interval_ms.value) : 18;
    spanned = split_node_to_delayed_spans(ts_node, interval);
    ts_node.innerHTML = "".concat(...spanned);
  });
}

window.addEventListener("scroll", reveal_scroll);
window.onload = reveal_init;


/* ===== template-level reveal tagging ===== */
/*
 * Template-level reveal tagging.
 *
 * Opts specific page templates into the animate.js reveal system without
 * editing the generic theme sections they are built from. Runs before
 * animate.js's window.onload (reveal_init), so the tagged elements get
 * split / revealed in the same pass.
 *
 * CEC-367 — News article (.news-article):
 *   - hero image  → diagonal reveal (reveal_fadein_image)
 *   - header / date / title (h1/h2/h6) → per-letter write-on
 *   - body blocks (paragraphs + inline images) → consistent fade-up
 *
 * CEC-368 — Styling article (/blogs/staff-styling/<handle>) + listing (/blogs/staff-styling):
 *   - article: gallery image → diagonal reveal; model name → per-letter;
 *     caption + featured-variant cards → fade-up
 *   - listing: cards stagger in via the theme's native reveal-on-scroll
 *
 * CEC-369 — Top page hero slideshows:
 *   - image → diagonal reveal; caption heading → per-letter write-on
 */
(function () {
  function ready(fn) {
    if (document.readyState !== 'loading') fn();
    else document.addEventListener('DOMContentLoaded', fn);
  }

  function stripTran(el) {
    el.className = el.className.replace(/\breveal_tran_\w+\b/g, '').replace(/\s+/g, ' ').trim();
  }

  function asLetter(el) {
    stripTran(el);
    el.classList.add('reveal_fadein_letter');
  }

  var REVEAL_DIRS = ['to bottom left', 'to bottom right', 'to top left', 'to top right'];

  function asImage(el) {
    stripTran(el);
    // Randomise which corner reveals first (per image, per load):
    // rt→lb, lt→rb, rb→lt, lb→rt. Heroes in view on load reveal on load,
    // the rest reveal on scroll. (No above-the-fold LCP skip — every hero
    // animates; revisit if Core Web Vitals become a concern.)
    // Heroes already in the viewport on load are pre-masked in CSS at first
    // paint (see the @media (prefers-reduced-motion: no-preference) block in
    // animate.css) so they don't flash visible→hidden when this deferred script
    // runs. The pre-mask uses the default direction (to bottom left), so we
    // must NOT randomise --reveal-dir here for in-view heroes — that would flip
    // the masked corner the instant this script runs, which is itself a flash.
    // They also get --quick (transition-duration 0.3s) so the reveal finishes
    // promptly. Below-the-fold heroes are masked long before they're seen, so
    // randomising their reveal direction is safe and adds variety.
    if (el.getBoundingClientRect().top < window.innerHeight) {
      el.classList.add('reveal_fadein_image', 'reveal_fadein_image--quick');
    } else {
      el.style.setProperty('--reveal-dir', REVEAL_DIRS[Math.floor(Math.random() * REVEAL_DIRS.length)]);
      el.classList.add('reveal_fadein_image');
    }
  }

  function asBlockUp(el) {
    stripTran(el);
    el.classList.add('reveal_tran_bt');
  }

  ready(function () {
    // Hero image diagonal reveal disabled per director preference (minimalistic
    // view). The asImage() calls below are commented out; per-letter text and
    // block fade-ups continue to animate. To re-enable, uncomment the
    // `if (X) asImage(X);` lines and restore the pre-mask block in animate.css.
    var news = document.querySelector('.news-article');
    if (news) {
      // var hero = news.querySelector('.shopify-section--image-with-text-overlay img');
      // if (hero) asImage(hero);

      news.querySelectorAll(
        '.shopify-section--rich-text .h1, .shopify-section--rich-text .h2, .shopify-section--rich-text .h6'
      ).forEach(asLetter);

      news.querySelectorAll('.shopify-section--rich-text .prose > div').forEach(asBlockUp);
    }

    var path = location.pathname;
    var stylingArticle = /\/blogs\/staff-styling\/[^/]+/.test(path) && !/\/tagged\//.test(path);

    if (stylingArticle) {
      var sa = document.querySelector('.fixed-width-wrapper');
      if (sa) {
        // var gimg = sa.querySelector('.product-gallery__media img') || sa.querySelector('.product-gallery img');
        // if (gimg) asImage(gimg);
        // Model name / height line → per-letter; caption etc. → fade-up.
        // The line now carries a link to the staff page and an Instagram icon
        // (CEC-508), and the letter split rebuilds an element from its text
        // alone, which would flatten both. So split the name, the slash and the
        // height on their own, fade the icon up with them, and leave the line
        // itself and the block wrapping it out of the fade-up.
        sa.querySelectorAll('.shopify-section--rich-text .subheading:not(.styling-model-heading)').forEach(asLetter);
        sa.querySelectorAll('.styling-model-heading__name, .styling-model-heading__sep, .styling-model-heading__height').forEach(asLetter);
        sa.querySelectorAll('.styling-model-heading__instagram').forEach(asBlockUp);
        sa.querySelectorAll('.shopify-section--rich-text .prose > div:not(:has(.styling-model-heading))').forEach(asBlockUp);
        // Featured-variant product cards (not theme-revealed) → staggered fade-up.
        sa.querySelectorAll('.shopify-section--featured-collections .product-card').forEach(asBlockUp);
      }
    }

    // Top page (CEC-369): hero image diagonal reveal disabled (see note above);
    // per-letter caption still animates.
    if (path === '/' || path === '') {
      document.querySelectorAll('.shopify-section--slideshow').forEach(function (sec) {
        // var simg = sec.querySelector('.content-over-media img') || sec.querySelector('img');
        // if (simg) asImage(simg);
        sec.querySelectorAll(
          '.slideshow__slide-content .h1, .slideshow__slide-content .h2, .slideshow__slide-content .subheading'
        ).forEach(asLetter);
      });
    }
  });
})();


/* ===== scroll reveals ===== */
/*
 * Scroll reveals.
 *
 * - Footer (every page): the policy link columns slide in from the left /
 *   right, while the newsletter, social icons, logo and copyright fade + rise.
 * - Home page only: the hero reveals from the top on load; later banners and
 *   section headings fade + rise as they enter view.
 *
 * Product / blog cards are left alone (the theme reveals those natively via
 * [reveal-on-scroll]).
 *
 * A scroll handler re-checks every target each frame so fast scrolls can't skip
 * an element and leave it stuck hidden. Motion uses the Web Animations API,
 * which — unlike CSS transitions on this page — isn't interrupted by the
 * theme's per-frame section work.
 */
(function () {
  var TEMPLATE = (document.currentScript && document.currentScript.getAttribute('data-template')) || '';
  var isIndex = TEMPLATE === 'index';

  if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
  if (!Element.prototype.animate) return;

  var KEYFRAMES = {
    rise:  [{ opacity: 0, transform: 'translateY(1.75rem)' },  { opacity: 1, transform: 'translateY(0)' }],
    down:  [{ opacity: 0, transform: 'translateY(-1.75rem)' }, { opacity: 1, transform: 'translateY(0)' }],
    left:  [{ opacity: 0, transform: 'translateX(-1.5rem)' },  { opacity: 1, transform: 'translateX(0)' }],
    right: [{ opacity: 0, transform: 'translateX(1.5rem)' },   { opacity: 1, transform: 'translateX(0)' }]
  };
  var HIDDEN = {
    rise: ['home-rise'],
    down: ['home-rise', 'home-rise--down'],
    left: ['home-rise', 'home-rise--left'],
    right: ['home-rise', 'home-rise--right']
  };

  function ready(fn) {
    if (document.readyState !== 'loading') fn();
    else document.addEventListener('DOMContentLoaded', fn);
  }

  ready(function () {
    var targets = [];
    function add(el, kind, delay) {
      if (!el) return;
      if (el.hasAttribute('reveal-on-scroll') || el.closest('[reveal-on-scroll]')) return; // theme handles these
      if (targets.some(function (t) { return t.el === el; })) return;
      targets.push({ el: el, kind: kind || 'rise', delay: delay || 0 });
    }

    // --- Home page sections (index template only) --------------------------
    var main = document.getElementById('main') || document.querySelector('main');
    if (isIndex && main) {
      main.querySelectorAll(':scope > .shopify-section').forEach(function (section, idx) {
        if (section.classList.contains('shopify-section--footer')) return;
        if (section.classList.contains('shopify-section--slideshow')) {
          // The hero (first slide) reveals from the top; later banners rise up.
          add(section.querySelector('.slideshow') || section.firstElementChild, idx === 0 ? 'down' : 'rise');
          return;
        }
        add(section.querySelector('.section-header, h2.h2, .h2, h2'), 'rise');
      });
    }

    // --- Footer ------------------------------------------------------------
    var footer = document.querySelector('.shopify-section--footer');
    if (footer) {
      add(footer.querySelector('.footer__social'), 'rise');
      add(footer.querySelector('.footer__block--newsletter'), 'rise');
      add(footer.querySelector('.footer__block--text'), 'rise');

      // Policy (legal) menu: intentionally NOT animated (CEC-399) — the
      // left/right column slide-in was removed; the links render statically.

      add(footer.querySelector('.footer__logo'), 'rise');
      add(footer.querySelector('.footer__aside p'), 'rise');
    }

    if (!targets.length) return;
    targets.forEach(function (t) {
      HIDDEN[t.kind].forEach(function (c) { t.el.classList.add(c); });
    });

    var pending = targets.slice();

    function reveal(t, animate) {
      HIDDEN[t.kind].forEach(function (c) { t.el.classList.remove(c); });
      if (animate) {
        t.el.animate(KEYFRAMES[t.kind], {
          duration: 800,
          delay: t.delay,
          easing: 'cubic-bezier(0.22, 0.61, 0.36, 1)',
          fill: 'backwards'
        });
      }
    }

    function check() {
      var vh = window.innerHeight;
      // Elements that sit permanently within the bottom 60px (e.g. the
      // copyright line) would never cross the trigger — reveal them once the
      // page is scrolled to the very bottom.
      var atBottom = (window.scrollY + vh) >= (document.documentElement.scrollHeight - 2);
      for (var i = pending.length - 1; i >= 0; i--) {
        var t = pending[i];
        var r = t.el.getBoundingClientRect();
        if (r.bottom <= 0) {
          pending.splice(i, 1);
          reveal(t, false); // already scrolled past — snap, no animation
        } else if (r.top < vh - 60 || (atBottom && r.top < vh)) {
          pending.splice(i, 1);
          reveal(t, true); // entering view — animate
        }
      }
      if (!pending.length) {
        window.removeEventListener('scroll', onScroll);
        window.removeEventListener('resize', onScroll);
      }
    }

    var ticking = false;
    function onScroll() {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(function () { ticking = false; check(); });
    }

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    check(); // reveal whatever is already in view on load
  });
})();


/* ===== product-card secondary image on touch (mobile) ===== */
(function () {
  document.addEventListener('DOMContentLoaded', function () {
    document.body.addEventListener('touchstart', function (e) {
      var card = e.target.closest('.product-card__figure:has(.product-card__image--secondary)');
      if (!card) return;
      var secondary = card.querySelector('.product-card__image--secondary');
      var primary = card.querySelector('.product-card__image--primary');
      secondary.style.opacity = 1;
      primary.style.opacity = 0;
      secondary.style.visibility = 'visible';
    });

    document.body.addEventListener('touchend', function (e) {
      var card = e.target.closest('.product-card__figure:has(.product-card__image--secondary)');
      if (!card) return;
      var secondary = card.querySelector('.product-card__image--secondary');
      var primary = card.querySelector('.product-card__image--primary');
      secondary.style.opacity = 0;
      primary.style.opacity = 1;
      secondary.style.visibility = 'hidden';
    });
  });
})();
